import { db } from '../firebase';
import {
  collection, addDoc, getDocs, query, where, orderBy,
  serverTimestamp, Timestamp, limit
} from 'firebase/firestore';

// ============================================
// ACTIVITY LOG SERVICE — CLOSd KPI Tracking
// ============================================
// Logs org-level events to /organizations/{orgId}/activityLog
// Used by the SuperAdmin KPI Dashboard to track:
//   Phase 1 — Activation (Days 1–7)
//   Phase 2 — Engagement (Weeks 1–3)
//   Phase 3 — Dependency (Weeks 4–6)
// ============================================

const getLogCollection = (orgId) =>
  collection(db, 'organizations', orgId, 'activityLog');

// ---- CORE LOGGING FUNCTION ----
const logActivity = async (orgId, type, userId, metadata = {}) => {
  try {
    await addDoc(getLogCollection(orgId), {
      type,
      userId: userId || 'system',
      timestamp: serverTimestamp(),
      metadata,
    });
    console.log(`[ACTIVITY LOG] ${type} logged for org ${orgId}`);
  } catch (err) {
    console.error(`[ACTIVITY LOG] Failed to log ${type}:`, err);
  }
};

// ---- SPECIFIC EVENT LOGGERS ----

export const logLogin = (orgId, userId) =>
  logActivity(orgId, 'login', userId);

export const logDealCreated = (orgId, userId, dealData) =>
  logActivity(orgId, 'dealCreated', userId, {
    projectId: dealData.projectId || '',
    dealType: dealData.type || '',
    clientName: dealData.clientName || '',
    propertyAddress: dealData.propertyAddress || '',
  });

export const logTaskCompleted = (orgId, userId, taskData) =>
  logActivity(orgId, 'taskCompleted', userId, {
    projectId: taskData.projectId || '',
    taskId: taskData.taskId || '',
    taskName: taskData.taskName || '',
  });

export const logDueDateSet = (orgId, userId, taskData) =>
  logActivity(orgId, 'dueDateSet', userId, {
    projectId: taskData.projectId || '',
    taskId: taskData.taskId || '',
    taskName: taskData.taskName || '',
    dueDate: taskData.dueDate || '',
  });

export const logSeatAdded = (orgId, userId, memberData) =>
  logActivity(orgId, 'seatAdded', userId, {
    memberId: memberData.memberId || '',
    memberName: memberData.memberName || '',
  });

export const logDealClosed = (orgId, userId, dealData) =>
  logActivity(orgId, 'dealClosed', userId, {
    projectId: dealData.projectId || '',
    clientName: dealData.clientName || '',
    propertyAddress: dealData.propertyAddress || '',
  });

export const logDealArchived = (orgId, userId, dealData) =>
  logActivity(orgId, 'dealArchived', userId, {
    projectId: dealData.projectId || '',
    clientName: dealData.clientName || '',
  });

export const logTaskCustomized = (orgId, userId, taskData) =>
  logActivity(orgId, 'taskCustomized', userId, {
    projectId: taskData.projectId || '',
    taskId: taskData.taskId || '',
    taskName: taskData.taskName || '',
    field: taskData.field || '',
  });

// ---- FETCH LOGS (for KPI dashboard) ----

export const getActivityLogs = async (orgId, limitCount = 500) => {
  const snap = await getDocs(
    query(
      getLogCollection(orgId),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    )
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getActivityLogsSince = async (orgId, sinceDate) => {
  const ts = Timestamp.fromDate(sinceDate);
  const snap = await getDocs(
    query(
      getLogCollection(orgId),
      where('timestamp', '>=', ts),
      orderBy('timestamp', 'asc')
    )
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};